import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, untracked } from '@angular/core';
import { environment } from '../environments/environment';
import { Store } from '@ngrx/store';
import { Router, RouterOutlet } from '@angular/router';
import { map } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { pageWithoutLayout } from './app.utils';
import { SvgIconRegistryService } from 'angular-svg-icon';
import { SubscriptionComponent } from './modules/shared/helpers/subscription.component';
import {
    contentGlobalErrors,
    contentGlobalInfo,
    contentGlobalSuccess,
    contentGlobalWarnings,
} from './modules/content/content.error';
import config from './config/config';
import { translate } from './modules/shared/shared.global-service';
import { GlobalStore } from './store/global.global-store';
import { selectUserSettings } from './modules/layout/store/layout.selectors';
import { L10n, loadCldr, setCulture, setCurrencyCode } from '@syncfusion/ej2-base';
import csNumberData from '@syncfusion/ej2-cldr-data/main/cs/numbers.json';
import cstimeZoneData from '@syncfusion/ej2-cldr-data/main/cs/timeZoneNames.json';
import csGregorian from '@syncfusion/ej2-cldr-data/main/cs/ca-gregorian.json';
import csNumberingSystem from '@syncfusion/ej2-cldr-data/supplemental/numberingSystems.json';
import * as EJ2_LOCALE from '@syncfusion/ej2-locale/src/cs.json';
import { MessageService, ToastMessageOptions } from '@openng/optimus-ui/api';
import { toObservable } from '@angular/core/rxjs-interop';
import { currentUserId } from './store/current-user-id';
import { ThemeService } from './modules/layout/services/theme.service';
import { defaultAppConfig } from './modules/content/service/types/user.types';
import { AppStore } from './app.store';
import { AppConfig, LayoutService } from './modules/layout/services/layout.service';
import { Deduplicator } from '@libs/shared/helpers/deduplicator';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ConfirmPopupModule } from '@openng/optimus-ui/confirmpopup';
import { ToastModule } from '@openng/optimus-ui/toast';
import { TranslatePipe } from '@ngx-translate/core';
import { LayoutComponent } from './modules/layout/layout.component';
import { EmbedLayoutComponent } from './modules/layout/embed/embed-layout.component';

// Syncfusion translations
loadCldr(csNumberData, cstimeZoneData, csGregorian, csNumberingSystem);
L10n.load({ cs: EJ2_LOCALE.cs });
L10n.load({
    'cs': {
        'pivotview': {
            'null': '-',
            'excel': 'Excel',
        },
        'schedule': {
            'timelineDay': 'Časová osa',
        },
    },
});
setCulture('cs');
setCurrencyCode('CZK');

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [AppStore],
    standalone: true,
    imports: [
        AsyncPipe,
        RouterOutlet,
        ToastModule,
        ConfirmPopupModule,
        ConfirmDialogModule,
        TranslatePipe,
        LayoutComponent,
        EmbedLayoutComponent,
    ],
})
export class AppComponent extends SubscriptionComponent implements OnInit {

    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly svgRegistry = inject(SvgIconRegistryService);
    private readonly themeService = inject(ThemeService);
    private readonly layoutService = inject(LayoutService);
    private readonly messageService = inject(MessageService);
    private readonly globalStore = inject(GlobalStore);
    private readonly appStore = inject(AppStore);
    backendUrl = `${environment.apiUrl}/crm`;

    protected userSettings = this.store.selectSignal(selectUserSettings);

    protected displayLayout$ = combineLatest([
        toObservable(currentUserId),
        this.router.events,
    ]).pipe(
        map(([userId, event]) => {
            if (!userId) return false;
            return !pageWithoutLayout(this.router.url);
        }),
    );

    private deduplicator = new Deduplicator();

    constructor() {
        super();
        this.subs.push(
            contentGlobalErrors.subscribe((message) => {
                console.error('ERROR: ', message);
                if (this.deduplicator.isUnique(message)) {
                    this.messageService.add(this.formMessage(message, 'error'));
                }
            }),
            contentGlobalWarnings.subscribe((message) => {
                if (this.deduplicator.isUnique(message)) {
                    this.messageService.add(this.formMessage(message, 'warn'));
                }
            }),
            contentGlobalSuccess.subscribe((message) => {
                if (this.deduplicator.isUnique(message)) {
                    this.messageService.add(this.formMessage(message));
                }
            }),
            contentGlobalInfo.subscribe((message) => {
                if (this.deduplicator.isUnique(message)) {
                    this.messageService.add(this.formMessage(message, 'info'));
                }
            }),
        );
        effect(() => {
            const settings = this.userSettings();
            if (!settings) return;

            this.globalStore.loggedUserStore.parseCustomColors(settings?.colors);
            this.globalStore.loggedUserStore.parseCustomSettings(settings?.customSettings);
        });
        effect(() => {
            const apiSuccess = this.appStore.globalGlobalStore.apiSuccess();
            const whoamiData = apiSuccess?.key === 'whoami' ? apiSuccess.data : null;
            if (!whoamiData) return;

            untracked(() => {
                this.globalStore.loggedUserStore.currentUser.set(whoamiData);
                this.globalStore.loggedUserStore.employments.set(whoamiData.employments || []);
                this.layoutService.config.set(whoamiData.settings as AppConfig || defaultAppConfig);
                this.themeService.setTheme(whoamiData.settings || defaultAppConfig);
            });
        });
    }

    public ngOnInit(): void {
        // return url from Microsoft / Gmail logins
        if (!window.location.href.includes('/logon')) {
            this.appStore.api.whoami().then();
        }

        // load svg icons into svg registry service
        combineLatest([
            this.svgRegistry.loadSvg('/assets/svg/envelope.svg', 'envelope'),
            this.svgRegistry.loadSvg('/assets/svg/envelope-open.svg', 'envelope-open'),
            this.svgRegistry.loadSvg('/assets/svg/gear.svg', 'gear'),
            this.svgRegistry.loadSvg('/assets/svg/trash3.svg', 'trash3'),
            this.svgRegistry.loadSvg('/assets/svg/bookmark.svg', 'bookmark'),
            this.svgRegistry.loadSvg('/assets/svg/bookmark-x.svg', 'bookmark-x'),
            this.svgRegistry.loadSvg('/assets/svg/arrow-return-left.svg', 'arrow-return-left'),
            this.svgRegistry.loadSvg('/assets/svg/asterisk.svg', 'asterisk'),
            this.svgRegistry.loadSvg('/assets/svg/house-door-fill.svg', 'house-door-fill'),
            this.svgRegistry.loadSvg('/assets/svg/person-circle.svg', 'person-circle'),
            this.svgRegistry.loadSvg('/assets/svg/bar-chart-line.svg', 'bar-chart-line'),
            this.svgRegistry.loadSvg('/assets/svg/table.svg', 'table'),
            this.svgRegistry.loadSvg('/assets/svg/hand-index-thumb.svg', 'hand-index-thumb'),
            this.svgRegistry.loadSvg('/assets/svg/arrow-clockwise.svg', 'arrow-clockwise'),
            this.svgRegistry.loadSvg('/assets/svg/fork-and-knife.svg', 'fork-and-knife'),
            this.svgRegistry.loadSvg('/assets/svg/slash-circle.svg', 'slash-circle'),
            this.svgRegistry.loadSvg('/assets/svg/wrench.svg', 'wrench'),
            this.svgRegistry.loadSvg('/assets/svg/reply-fill.svg', 'reply-fill'),
            this.svgRegistry.loadSvg('/assets/svg/reply-fill-mirrored.svg', 'reply-fill-mirrored'),
            this.svgRegistry.loadSvg('/assets/svg/clipboard2-pulse.svg', 'clipboard2-pulse'),
            this.svgRegistry.loadSvg('/assets/svg/person-lines-fill.svg', 'person-lines-fill'),
            this.svgRegistry.loadSvg('/assets/svg/person-x-fill.svg', 'person-x-fill'),
            this.svgRegistry.loadSvg('/assets/svg/person-check-fill.svg', 'person-check-fill'),
            this.svgRegistry.loadSvg('/assets/svg/person.svg', 'person'),
            this.svgRegistry.loadSvg('/assets/svg/life-preserver.svg', 'life-preserver'),

            // table filters
            this.svgRegistry.loadSvg('/assets/svg/filters/filter.svg', 'filter'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-off.svg', 'filterOff'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-x.svg', 'filterX'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-search.svg', 'filterSearch'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-like.svg', 'filterLike'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-nlike.svg', 'filterNlike'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-starts.svg', 'filterStarts'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-ends.svg', 'filterEnds'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-eq.svg', 'filterEq'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-neq.svg', 'filterNeq'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-lt.svg', 'filterLt'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-lte.svg', 'filterLte'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-gt.svg', 'filterGt'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-gte.svg', 'filterGte'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-between.svg', 'filterBetween'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-nbetween.svg', 'filterNbetween'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-in.svg', 'filterIn'),
            this.svgRegistry.loadSvg('/assets/svg/filters/filter-nin.svg', 'filterNin'),

            // file types
            this.svgRegistry.loadSvg('/assets/svg/filetype-bmp.svg', 'bmp'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-csv.svg', 'csv'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-doc.svg', 'doc'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-docx.svg', 'docx'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-heic.svg', 'heic'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-jpg.svg', 'jpg'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-json.svg', 'json'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-pdf.svg', 'pdf'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-png.svg', 'png'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-txt.svg', 'txt'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-xls.svg', 'xls'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-xlsx.svg', 'xlsx'),
            this.svgRegistry.loadSvg('/assets/svg/filetype-xml.svg', 'xml'),
            this.svgRegistry.loadSvg('/assets/svg/file-earmark-arrow-down.svg', 'unknown'),

            // medical measuring symbols
            this.svgRegistry.loadSvg('/assets/svg/caret-up.svg', 'systolicPressure'),
            this.svgRegistry.loadSvg('/assets/svg/caret-down.svg', 'diastolicPressure'),
            this.svgRegistry.loadSvg('/assets/svg/heart-pulse.svg', 'pulse'),
            this.svgRegistry.loadSvg('/assets/svg/lungs.svg', 'breath'),
            this.svgRegistry.loadSvg('/assets/svg/droplet.svg', 'glycemia'),
            this.svgRegistry.loadSvg('/assets/svg/percent.svg', 'spo2'),
            this.svgRegistry.loadSvg('/assets/svg/emoji-neutral.svg', 'pain'),
            this.svgRegistry.loadSvg('/assets/svg/speedometer2.svg', 'weight'),
            this.svgRegistry.loadSvg('/assets/svg/thermometer-half.svg', 'temperature'),
            this.svgRegistry.loadSvg('/assets/svg/hand-index-thumb.svg', 'usedGloves'),
            this.svgRegistry.loadSvg('/assets/svg/virus2.svg', 'woundSize'),
            this.svgRegistry.loadSvg('/assets/svg/measuring-cup.svg', 'measuringCup'),

            // scheduler event statuses
            this.svgRegistry.loadSvg('/assets/svg/chevron-double-left.svg', 'eventSpannedFrom'),
            this.svgRegistry.loadSvg('/assets/svg/chevron-double-right.svg', 'eventSpannedTo'),
            this.svgRegistry.loadSvg('/assets/svg/chevron-double-up.svg', 'eventSpannedFromVertical'),
            this.svgRegistry.loadSvg('/assets/svg/chevron-double-down.svg', 'eventSpannedToVertical'),
            this.svgRegistry.loadSvg('/assets/svg/repeat.svg', 'eventRecurrence'),
            this.svgRegistry.loadSvg('/assets/svg/repeat-1.svg', 'eventRecurrenceException'),

            // scheduler event transport types
            this.svgRegistry.loadSvg('/assets/svg/person-walking.svg', 'transportWalk'),
            this.svgRegistry.loadSvg('/assets/svg/bicycle.svg', 'transportBike'),
            this.svgRegistry.loadSvg('/assets/svg/scooter.svg', 'transportScooter'),
            this.svgRegistry.loadSvg('/assets/svg/car-front-fill.svg', 'transportCar'),
            this.svgRegistry.loadSvg('/assets/svg/bus-front-fill.svg', 'transportBus'),
            this.svgRegistry.loadSvg('/assets/svg/train-lightrail-front-fill.svg', 'transportTrain'),

            // signature states icons
            this.svgRegistry.loadSvg('/assets/svg/shield-fill-check.svg', 'signatureValid'),
            this.svgRegistry.loadSvg('/assets/svg/shield-slash.svg', 'signatureNotValid'),
            this.svgRegistry.loadSvg('/assets/svg/shield-exclamation.svg', 'signatureWarning'),
            this.svgRegistry.loadSvg('/assets/svg/shield-lock-fill.svg', 'signatureSigned'),
            this.svgRegistry.loadSvg('/assets/svg/shield-lock.svg', 'signatureNotSigned'),

            // jodit pro wysiwyg
            this.svgRegistry.loadSvg('/assets/svg/clipboard-plus.svg', 'jodit-add-templates'),

            // microsoft login button
            this.svgRegistry.loadSvg('/assets/svg/microsoft.svg', 'microsoft'),
        ]).subscribe();
    }

    private formMessage(message: string, severity: string = 'success'): ToastMessageOptions {
        // Those checks are required due to some errors coming as CS messages from be
        let msg = translate.instant(message);
        const summary = translate.instant(`messages.severity.${severity}`);
        if (severity === 'error' && msg === message) {
            msg = translate.instant(`errors.${message}`);
            if (msg.includes('errors.')) msg = message;
        }

        return {
            key: 'global-toasts',
            severity,
            summary,
            detail: msg,
            closable: true,
            life: config.messageLifetime,
        } as ToastMessageOptions;
    }
}
